/**
 * Role hierarchy and permission checking utilities
 */

export type UserRole = 'admin' | 'underwriter' | 'viewer'

/**
 * Role hierarchy - higher roles have access to lower role permissions
 */
const ROLE_HIERARCHY: Record<UserRole, number> = {
  admin: 3,
  underwriter: 2,
  viewer: 1,
}

/**
 * Check if a user role has access to required roles
 * @param userRole - The user's current role
 * @param requiredRoles - Array of roles that can access the resource
 * @returns true if user has access
 */
export const checkRole = (
  userRole: UserRole | null,
  requiredRoles: UserRole[]
): boolean => {
  if (!userRole) return false

  // Check if user's role is in the required roles list
  if (requiredRoles.includes(userRole)) {
    return true
  }

  // Check if user's role is higher than any required role
  const userLevel = ROLE_HIERARCHY[userRole]
  return requiredRoles.some(role => userLevel >= ROLE_HIERARCHY[role])
}

/**
 * Get role display name
 */
export const getRoleDisplayName = (role: UserRole): string => {
  const roleNames: Record<UserRole, string> = {
    admin: 'Admin',
    underwriter: 'Underwriter',
    viewer: 'Viewer',
  }
  return roleNames[role] || role
}

/**
 * Role-based permissions
 */
export const PERMISSIONS = {
  // Admin permissions
  ADMIN: {
    manageUsers: true,
    assignZones: true,
    manageAllScores: true,
    fullSystemAccess: true,
    viewZones: true,
    editZones: true,
    deleteZones: true,
    searchLocations: true,
    viewData: true,
  },
  // Underwriter permissions
  UNDERWRITER: {
    manageUsers: false,
    assignZones: false,
    manageAllScores: false,
    fullSystemAccess: false,
    viewZones: true,
    editZones: true,
    deleteZones: false,
    searchLocations: true,
    viewData: true,
    addModifyScores: true,
  },
  // Viewer permissions
  VIEWER: {
    manageUsers: false,
    assignZones: false,
    manageAllScores: false,
    fullSystemAccess: false,
    viewZones: true,
    editZones: false,
    deleteZones: false,
    searchLocations: true,
    viewData: true,
    addModifyScores: false,
  },
} as const
